import { createContext, useContext, useState, useCallback, useRef } from 'react'
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react'

/*
 * Système de toast léger, sans dépendance, stylé sur les tokens du design system
 * (var(--status-*), var(--info-*), var(--bg-card)…). Remplace les alert() natifs.
 *
 * Usage :
 *   const toast = useToast()
 *   toast.success('Dossier créé')
 *   toast.error('Échec de l’envoi')   // role="alert" + aria-live assertive
 *   toast.info('…', { duration: 6000 })
 */

const ToastContext = createContext(null)

const VARIANTS = {
  success: { Icon: CheckCircle2, fg: 'var(--status-ok)',   bg: 'var(--status-ok-soft)',   line: 'var(--status-ok-line)',   role: 'status', live: 'polite' },
  error:   { Icon: AlertTriangle, fg: 'var(--status-miss)', bg: 'var(--status-miss-soft)', line: 'var(--status-miss-line)', role: 'alert',  live: 'assertive' },
  info:    { Icon: Info,          fg: 'var(--info)',        bg: 'var(--info-soft)',        line: 'var(--info-line)',        role: 'status', live: 'polite' },
}

let idCounter = 0

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const timers = useRef({})

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id))
    const tm = timers.current[id]
    if (tm) { clearTimeout(tm); delete timers.current[id] }
  }, [])

  const push = useCallback((type, message, opts = {}) => {
    const id = ++idCounter
    const duration = opts.duration ?? 4000
    setToasts((list) => [...list, { id, type, message }])
    if (duration > 0) timers.current[id] = setTimeout(() => dismiss(id), duration)
    return id
  }, [dismiss])

  const api = {
    success: (m, o) => push('success', m, o),
    error: (m, o) => push('error', m, o),
    info: (m, o) => push('info', m, o),
    dismiss,
  }

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 9999,
          display: 'flex', flexDirection: 'column', gap: 8,
          maxWidth: 'min(380px, calc(100vw - 40px))', pointerEvents: 'none',
        }}
      >
        {toasts.map((t) => {
          const v = VARIANTS[t.type] || VARIANTS.info
          const { Icon } = v
          return (
            <div
              key={t.id}
              role={v.role}
              aria-live={v.live}
              aria-atomic="true"
              style={{
                pointerEvents: 'auto',
                display: 'flex', alignItems: 'flex-start', gap: 10,
                background: 'var(--bg-card)',
                border: `1px solid ${v.line}`,
                borderLeft: `3px solid ${v.fg}`,
                borderRadius: 10,
                padding: '11px 12px 11px 13px',
                boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                animation: 'fadeIn 0.18s ease',
              }}
            >
              <span style={{ flexShrink: 0, color: v.fg, display: 'inline-flex', marginTop: 1 }} aria-hidden="true">
                <Icon size={17} />
              </span>
              <span style={{ flex: 1, fontSize: '0.82rem', lineHeight: 1.45, color: 'var(--text-primary)' }}>
                {t.message}
              </span>
              <button
                onClick={() => dismiss(t.id)}
                aria-label="Fermer la notification"
                style={{
                  flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-faint)', display: 'inline-flex', padding: 1, marginTop: 1,
                }}
              >
                <X size={15} />
              </button>
            </div>
          )
        })}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    // Fallback défensif si le Provider est absent : on ne casse pas l’app.
    const noop = (m) => { if (typeof console !== 'undefined') console.warn('[toast] hors ToastProvider:', m) }
    return { success: noop, error: noop, info: noop, dismiss: () => {} }
  }
  return ctx
}

export default ToastProvider
